import React, {useState, useEffect, useMemo, useCallback} from 'react';
import {useParams, Link, Navigate, useNavigate} from 'react-router-dom';
import {isAuthenticated} from '../api/auth';
import {
    fetchCardGroup,
    fetchGroupCards,
    fetchCardsProgress,
    deleteCardGroup,
    getReviewCards
} from '../api/learning-cards';
import {LEARNING_CARDS_ROUTES} from '../constants/learning-cards';
import {WORDS} from '../constants/words';
import { CARD_MARK_LABELS, CARD_MARK_COLORS } from '../constants/learning-cards';
import EditGroupModal from '../components/EditGroupModal';
import ConfirmationModal from '../components/ConfirmationModal';
import FlashCard from '../components/FlashCard';
import AddCardModal from '../components/AddCardModal';
import StartTestModal from '../components/StartTestModal';

function GroupDetailPage() {
    const {groupId} = useParams();
    const navigate = useNavigate();
    const [authenticated, setAuthenticated] = useState(isAuthenticated());
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [group, setGroup] = useState(null);
    const [cards, setCards] = useState([]);
    const [filteredCards, setFilteredCards] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedProgressFilter, setSelectedProgressFilter] = useState(new Set());
    const [searchIndex, setSearchIndex] = useState(new Map());
    const [sortOrder, setSortOrder] = useState(() => {
        return localStorage.getItem('cardsSortOrder') || 'progress-asc';
    });
    const [cardsProgress, setCardsProgress] = useState(new Map());
    const [showAddCardModal, setShowAddCardModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showStartTestModal, setShowStartTestModal] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    useEffect(() => {
        const authStatus = isAuthenticated();
        setAuthenticated(authStatus);

        if (authStatus && groupId) {
            loadGroupAndCards();
        } else {
            setLoading(false);
        }

        const handleStorageChange = () => {
            const newAuthStatus = isAuthenticated();
            setAuthenticated(newAuthStatus);
        };

        window.addEventListener('storage', handleStorageChange);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
        };
    }, [groupId]);

    useEffect(() => {
        buildSearchIndex();
    }, [cards]);

    useEffect(() => {
        filterCards();
    }, [searchQuery, selectedProgressFilter, searchIndex, sortOrder]);

    const buildSearchIndex = () => {
        const index = new Map();

        cards.forEach(card => {
            let side1Text = '';
            let side2Text = '';
            let side1Readings = [];
            let side2Readings = [];

            if (card.sides && Array.isArray(card.sides) && card.sides.length >= 2) {
                side1Text = card.sides[0]?.text || '';
                side2Text = card.sides[1]?.text || '';
                side1Readings = card.sides[0]?.reading_pairs || card.sides[0]?.readingPairs || [];
                side2Readings = card.sides[1]?.reading_pairs || card.sides[1]?.readingPairs || [];
            } else {
                side1Text = card.frontText || card.front_text || '';
                side2Text = card.backText || card.back_text || '';
            }

            const allReadings = [...side1Readings, ...side2Readings]
                .filter(pair => pair && typeof pair === 'object')
                .map(pair => `${pair.text || ''} ${pair.reading || ''}`)
                .join(' ');

            const searchableText = `${side1Text} ${side2Text} ${allReadings}`.toLowerCase();
            index.set(card.id, searchableText);
        });

        setSearchIndex(index);
    };

    const sortCards = useCallback((cards) => {
        const cardsCopy = [...cards];

        switch (sortOrder) {
            case 'progress-asc':
                return cardsCopy.sort((a, b) => {
                    const progressA = cardsProgress.get(a.id);
                    const progressB = cardsProgress.get(b.id);
                    const markA = progressA?.mark || 'MARK_NULL';
                    const markB = progressB?.mark || 'MARK_NULL';

                    const orderMap = {
                        'MARK_NULL': 0,
                        'MARK_E': 1,
                        'MARK_D': 2,
                        'MARK_C': 3,
                        'MARK_B': 4,
                        'MARK_A': 5
                    };

                    return (orderMap[markA] || 0) - (orderMap[markB] || 0);
                });

            case 'progress-desc':
                return cardsCopy.sort((a, b) => {
                    const progressA = cardsProgress.get(a.id);
                    const progressB = cardsProgress.get(b.id);
                    const markA = progressA?.mark || 'MARK_NULL';
                    const markB = progressB?.mark || 'MARK_NULL';

                    const orderMap = {
                        'MARK_NULL': 0,
                        'MARK_E': 1,
                        'MARK_D': 2,
                        'MARK_C': 3,
                        'MARK_B': 4,
                        'MARK_A': 5
                    };

                    return (orderMap[markB] || 0) - (orderMap[markA] || 0);
                });

            case 'created-asc':
                return cardsCopy.sort((a, b) => {
                    const dateA = new Date(a.created_at || a.createdAt || 0);
                    const dateB = new Date(b.created_at || b.createdAt || 0);
                    return dateA - dateB;
                });

            case 'created-desc':
                return cardsCopy.sort((a, b) => {
                    const dateA = new Date(a.created_at || a.createdAt || 0);
                    const dateB = new Date(b.created_at || b.createdAt || 0);
                    return dateB - dateA;
                });

            default:
                return cardsCopy;
        }
    }, [sortOrder, cardsProgress]);

    const filterCards = () => {
        if (searchIndex.size === 0) {
            setFilteredCards([]);
            return;
        }

        let filtered = cards;

        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase().trim();
            filtered = filtered.filter(card => {
                const searchableText = searchIndex.get(card.id) || '';
                return searchableText.includes(query);
            });
        }

        if (selectedProgressFilter.size > 0) {
            filtered = filtered.filter(card => {
                const progress = cardsProgress.get(card.id);
                const mark = progress?.mark || 'MARK_NULL';
                return selectedProgressFilter.has(mark);
            });
        }

        const sorted = sortCards(filtered);
        setFilteredCards(sorted);
    };

    const handleSearchChange = useCallback((e) => {
        setSearchQuery(e.target.value);
    }, []);

    const clearSearch = useCallback(() => {
        setSearchQuery('');
    }, []);

    const getProgressCounts = useMemo(() => {
        const counts = {
            'MARK_NULL': 0,
            'MARK_A': 0,
            'MARK_B': 0,
            'MARK_C': 0,
            'MARK_D': 0,
            'MARK_E': 0
        };

        cards.forEach(card => {
            const progress = cardsProgress.get(card.id);
            const mark = progress?.mark || 'MARK_NULL';
            counts[mark]++;
        });

        return counts;
    }, [cards, cardsProgress]);

    const getProgressEmoji = useCallback((mark) => {
        const emojiMap = {
            'MARK_NULL': '⚪',
            'MARK_A': '🟢',
            'MARK_B': '🔵',
            'MARK_C': '🟡',
            'MARK_D': '🔴',
            'MARK_E': '⚫'
        };
        return emojiMap[mark] || '⚪';
    }, []);

    const toggleProgressFilter = useCallback((mark) => {
        setSelectedProgressFilter(prev => {
            const newFilter = new Set(prev);
            if (newFilter.has(mark)) {
                newFilter.delete(mark);
            } else {
                newFilter.add(mark);
            }
            return newFilter;
        });
    }, []);

    const clearProgressFilter = useCallback(() => {
        setSelectedProgressFilter(new Set());
    }, []);

    const handleSortChange = useCallback((e) => {
        const newSortOrder = e.target.value;
        setSortOrder(newSortOrder);
        localStorage.setItem('cardsSortOrder', newSortOrder);
    }, []);

    const getSortOptions = () => {
        return [
            { value: 'progress-asc', label: 'Progress: New → A' },
            { value: 'progress-desc', label: 'Progress: A → New' },
            { value: 'created-asc', label: 'Created: Oldest first' },
            { value: 'created-desc', label: 'Created: Newest first' }
        ];
    };

    const loadGroupAndCards = async () => {
        setLoading(true);
        setError('');

        try {
            const groupResult = await fetchCardGroup(groupId);

            if (!groupResult.success) {
                setError(groupResult.message || 'Failed to load group details');
                setLoading(false);
                return;
            }

            console.log('Loaded group data:', groupResult.data);
            setGroup(groupResult.data.group);
            console.log('Group set to state:', groupResult.data.group);

            setGroup(groupResult.data.group);

            const [cardsResult, progressResult] = await Promise.all([
                fetchGroupCards(groupId),
                fetchCardsProgress(groupId)
            ]);

            console.log('Cards result:', cardsResult);
            console.log('Progress result:', progressResult);

            if (cardsResult.success) {
                setCards(cardsResult.data.cards || []);
            } else {
                console.error('Error loading cards:', cardsResult.message);
            }

            if (progressResult.success) {
                const progressMap = new Map();
                console.log('Progress data received:', progressResult.data);
                (progressResult.data.cards || []).forEach(progress => {
                    console.log('Processing progress:', progress);
                    const cardId = progress.cardId || progress.card_id || progress.id;
                    console.log('Mapping card ID:', cardId, 'to progress:', progress);
                    progressMap.set(cardId, progress);
                });
                console.log('Final progress map:', progressMap);
                setCardsProgress(progressMap);
            } else {
                console.error('Error loading progress:', progressResult.message);
            }
        } catch (err) {
            setError('An unexpected error occurred. Please try again.');
            console.error('Load group error:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteGroup = async () => {
        setIsDeleting(true);
        try {
            const result = await deleteCardGroup(groupId);

            if (result.success) {
                navigate(LEARNING_CARDS_ROUTES.GROUPS);
            } else {
                setError(result.message || 'Failed to delete group');
                setShowDeleteModal(false);
            }
        } catch (err) {
            setError('An unexpected error occurred. Please try again.');
            console.error('Delete group error:', err);
            setShowDeleteModal(false);
        } finally {
            setIsDeleting(false);
        }
    };

    const handleStartTest = async (cardsCount, displayMode) => {
        console.log('Starting test with:', {cardsCount, displayMode, groupId});

        try {
            const result = await getReviewCards(groupId, cardsCount);
            console.log('getReviewCards result:', result);

            if (result.success && result.data.cards.length > 0) {
                console.log('Using review cards:', result.data.cards);
                navigate(`/learning-cards/groups/${groupId}/test`, {
                    state: {
                        reviewCards: result.data.cards,
                        displayMode: displayMode,
                        groupCardSideTypes: group?.cardSideTypes || group?.card_side_types || []
                    }
                });
            } else {
                console.log('No review cards available, trying to use all group cards');

                const groupCardsResult = await fetchGroupCards(groupId);
                console.log('Group cards result:', groupCardsResult);

                if (groupCardsResult.success && groupCardsResult.data.cards.length > 0) {
                    const cardsToUse = groupCardsResult.data.cards.slice(0, cardsCount);
                    console.log('Using group cards for test:', cardsToUse);

                    navigate(`/learning-cards/groups/${groupId}/test`, {
                        state: {
                            reviewCards: cardsToUse,
                            displayMode: displayMode,
                            groupCardSideTypes: group?.cardSideTypes || group?.card_side_types || []
                        }
                    });
                } else {
                    setError('No cards available for testing. Please add some cards to this group first.');
                    setShowStartTestModal(false);
                }
            }
        } catch (err) {
            console.error('Start test error:', err);
            setError('An unexpected error occurred. Please try again.');
            setShowStartTestModal(false);
        }
    };

    if (!authenticated) {
        return <Navigate to={LEARNING_CARDS_ROUTES.BASE} replace/>;
    }

    return (
        <div className="page-section">
            <div className="mb-3 d-flex justify-content-end align-items-center">
                <Link to={LEARNING_CARDS_ROUTES.GROUPS} className="btn btn-outline-secondary">
                    &larr; Back to Groups
                </Link>
            </div>

            {error && (
                <div className="alert alert-danger" role="alert">
                    {error}
                </div>
            )}

            {loading ? (
                <div className="text-center my-5">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            ) : (
                <>
                    {group ? (
                        <>
                            <div
                                className="card mb-4"
                                style={{ cursor: 'pointer' }}
                                onClick={() => setShowEditModal(true)}
                            >
                                <div className="card-body hover-bg-light">
                                    <div className="d-flex justify-content-between align-items-start mb-3">
                                        <div>
                                            <h2 className="card-title mb-1">{group.name}</h2>
                                            {group.description && (
                                                <p className="text-muted">{group.description}</p>
                                            )}
                                        </div>
                                    </div>

                                    <div className="d-flex gap-3 text-muted small">
                                        <div>Created: {new Date(group.createdAt).toLocaleDateString()}</div>
                                        <div>Visibility: {group.visibility}</div>
                                    </div>
                                </div>
                            </div>

                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h3>Cards: {filteredCards.length}{cards.length !== filteredCards.length ? ` / ${cards.length}` : ''}</h3>
                                <div className="d-flex align-items-center gap-2">
                                    {group && cards.length > 0 && (
                                        <button
                                            className="btn btn-success"
                                            onClick={() => setShowStartTestModal(true)}
                                        >
                                            Start Test
                                        </button>
                                    )}
                                    <button
                                        className="btn btn-primary"
                                        onClick={() => setShowAddCardModal(true)}
                                    >
                                        {WORDS.BUTTON_ADD_CARD}
                                    </button>
                                </div>
                            </div>

                            {cards.length > 0 && (
                                <>
                                    <div className="mb-3">
                                        <div className="d-flex justify-content-between align-items-center">
                                            <div className="d-flex flex-wrap gap-2">
                                                {Object.entries(getProgressCounts)
                                                    .filter(([mark, count]) => count > 0)
                                                    .map(([mark, count]) => {
                                                        const label = CARD_MARK_LABELS[mark] || 'Unknown';
                                                        const emoji = getProgressEmoji(mark);
                                                        const isSelected = selectedProgressFilter.has(mark);

                                                        return (
                                                            <button
                                                                key={mark}
                                                                className={`btn btn-sm ${isSelected ? 'btn-primary' : 'btn-outline-secondary'}`}
                                                                onClick={() => toggleProgressFilter(mark)}
                                                                style={{
                                                                    minWidth: '80px',
                                                                    transform: isSelected ? 'scale(1.05)' : 'scale(1)',
                                                                    transition: 'all 0.2s ease'
                                                                }}
                                                            >
                                                                {emoji} {label} ({count})
                                                            </button>
                                                        );
                                                    })}
                                            </div>
                                            {selectedProgressFilter.size > 0 && (
                                                <button
                                                    className="btn btn-link btn-sm p-0 text-decoration-none"
                                                    onClick={clearProgressFilter}
                                                >
                                                    Clear
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                    <div className="mb-4">
                                        <div className="row g-2 align-items-center">
                                            <div className="col-auto">
                                                <select
                                                    id="sortOrder"
                                                    className="form-select"
                                                    value={sortOrder}
                                                    onChange={handleSortChange}
                                                    style={{ minWidth: '180px' }}
                                                >
                                                    {getSortOptions().map(option => (
                                                        <option key={option.value} value={option.value}>
                                                            {option.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div className="col">
                                                <div className="input-group">
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        placeholder="Search..."
                                                        value={searchQuery}
                                                        onChange={handleSearchChange}
                                                    />
                                                    {searchQuery && (
                                                        <button
                                                            className="btn btn-outline-secondary"
                                                            type="button"
                                                            onClick={clearSearch}
                                                            title="Clear search"
                                                        >
                                                            ✕
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        {(searchQuery || selectedProgressFilter.size > 0) && filteredCards.length === 0 && (
                                            <div className="text-muted small mt-2">
                                                No cards found matching the current filters
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}

                            {cards.length === 0 ? (
                                <div className="card">
                                    <div className="card-body text-center py-5">
                                        <p className="mb-3">No cards in this group yet.</p>
                                        <button
                                            className="btn btn-primary"
                                            onClick={() => setShowAddCardModal(true)}
                                        >
                                            Add Your First Card
                                        </button>
                                    </div>
                                </div>
                            ) : filteredCards.length === 0 ? (
                                <div className="card">
                                    <div className="card-body text-center py-5">
                                        <p className="mb-3">No cards match your current filters.</p>
                                        <div className="d-flex justify-content-center gap-2">
                                            {searchQuery && (
                                                <button
                                                    className="btn btn-outline-secondary"
                                                    onClick={clearSearch}
                                                >
                                                    Clear Search
                                                </button>
                                            )}
                                            {selectedProgressFilter.size > 0 && (
                                                <button
                                                    className="btn btn-outline-secondary"
                                                    onClick={clearProgressFilter}
                                                >
                                                    Clear Progress Filter
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="d-flex justify-content-center">
                                    <div style={{maxWidth: '1200px', width: '100%'}}>
                                        <div
                                            className="row row-cols-2 row-cols-md-3 row-cols-lg-4 g-3 justify-content-center">
                                            {filteredCards.map(card => (
                                                <div key={card.id} className="col">
                                                    <FlashCard
                                                        card={card}
                                                        cardProgress={cardsProgress.get(card.id)}
                                                        group={group}
                                                        onCardUpdated={loadGroupAndCards}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}

                            <AddCardModal
                                show={showAddCardModal}
                                groupId={groupId}
                                group={group}
                                onClose={() => setShowAddCardModal(false)}
                                onCardAdded={loadGroupAndCards}
                            />

                            <EditGroupModal
                                show={showEditModal}
                                group={group}
                                onClose={() => setShowEditModal(false)}
                                onGroupUpdated={loadGroupAndCards}
                                onDeleteRequested={() => {
                                    setShowEditModal(false);
                                    setShowDeleteModal(true);
                                }}
                            />

                            <ConfirmationModal
                                show={showDeleteModal}
                                title="Delete Group"
                                message={`Are you sure you want to delete the group "${group?.name}"? This action cannot be undone and all cards in this group will be deleted.`}
                                confirmText="Delete Group"
                                cancelText="Cancel"
                                onConfirm={handleDeleteGroup}
                                onCancel={() => setShowDeleteModal(false)}
                                isLoading={isDeleting}
                            />

                            <StartTestModal
                                show={showStartTestModal}
                                group={group}
                                groupId={groupId}
                                onClose={() => setShowStartTestModal(false)}
                                onTestStart={handleStartTest}
                            />
                        </>
                    ) : (
                        <div className="alert alert-warning">
                            Group not found or you don't have permission to view it.
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

export default GroupDetailPage;